import cx from 'classnames'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import Styles from './InstagramReelPreview.module.scss'
import { ExternalIntegrationEnum, InstagramIntegration } from 'api/clients/ExternalIntegrationsClient'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { CheckIcon } from '../InstagramPreview/icons'
import { useTruncateText } from '../hooks/useTruncateText'
import { highlightContent } from '../highlightContent'
import { Button } from 'blaze-ui/Button'
import { Edit1 } from 'blaze-ui/icons'
import { CaptionSkeleton } from '../components/CaptionSkeleton/CaptionSkeleton'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'

interface Props {
  integration?: InstagramIntegration
  content?: string
  post?: ScheduledPostShow
  onEdit?: () => void
  isGenerating?: boolean
  alwaysTruncateCaption?: boolean
}

interface MoreProps {
  isTruncated: boolean
  setIsTruncated: React.Dispatch<React.SetStateAction<boolean>>
}

export const MoreButton = ({ isTruncated, setIsTruncated }: MoreProps) => {
  return (
    <button className={cx(Styles.moreButton)} onClick={() => setIsTruncated(!isTruncated)}>
      {isTruncated ? 'more' : 'less'}
    </button>
  )
}

export const Footer = ({ integration, content, onEdit, isGenerating, alwaysTruncateCaption = false, post }: Props) => {
  const { name, username, avatar } = integration
    ? extractProfileInfo(integration)
    : { name: 'User', username: 'user', avatar: Silhouette }

  const highlightClassNames = {
    mentionClassName: Styles.highlight,
    hashtagClassName: Styles.highlight,
  }

  const { isTruncated, setIsTruncated, shouldShowMoreButton, textRef } = useTruncateText({
    content: content || '',
    truncatedByDefault: alwaysTruncateCaption,
  })

  const showMoreButton = content && shouldShowMoreButton

  const postContent = post?.scheduled_post_targets.find(
    (target) => target.provider === ExternalIntegrationEnum.Instagram,
  )?.target_overrides?.content

  return (
    <div className={Styles.footer}>
      <div className={Styles.nameAndCaption}>
        <div className={Styles.nameRow}>
          <img src={avatar || Silhouette} alt={username} className={Styles.avatar} />
          <div className={Styles.name}>{name}</div>
          <CheckIcon />
          <div className={cx(Styles.name, Styles.follow)}>Follow</div>
          {onEdit && (
            <Button variant="tertiary" onPress={onEdit} frontIcon={Edit1}>
              Edit
            </Button>
          )}
        </div>
        <div className={Styles.caption}>
          {isGenerating ? (
            <CaptionSkeleton />
          ) : (
            <>
              <span
                ref={textRef as React.RefObject<HTMLSpanElement>}
                key={`caption-${content || ''}`}
                className={cx({ [Styles.truncated]: isTruncated })}
                dangerouslySetInnerHTML={{
                  __html: highlightContent(postContent || content || '', highlightClassNames),
                }}
              />
              {showMoreButton && !alwaysTruncateCaption && isTruncated && (
                <MoreButton isTruncated={isTruncated} setIsTruncated={setIsTruncated} />
              )}
            </>
          )}
        </div>
        {!isGenerating && showMoreButton && !alwaysTruncateCaption && !isTruncated && (
          <div className={Styles.lessButton}>
            <MoreButton isTruncated={isTruncated} setIsTruncated={setIsTruncated} />
          </div>
        )}
      </div>
    </div>
  )
}
